import { useContext, useEffect, useState } from 'react';

import { ImageModalContext } from '../providers';
import { ChatBlkQueryService, ChatBlkTelemetryService } from '../services';
import { telemetryFunctionMap } from '../types';
import { getStore } from '../utils';

interface ServerMessageImageProps {
    src: string;
    alt: string;
    queryResponseId: string;
    onImgLoad?: () => void;
}

/**
 * A React component that displays an image loaded from a blob source URL.
 * The image is clickable and opens a modal when clicked, while also tracking telemetry events.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.src - The source URL of the image to be loaded.
 * @param {string} props.alt - The alternative text for the image.
 * @param {string} props.queryResponseId - The identifier for the query response, used for telemetry tracking.
 * @param {() => void} props.onImgLoad - Callback function triggered when the image is successfully loaded.
 *
 * @returns {JSX.Element} A React node representing the image element.
 *
 * @remarks
 * - The component uses the `useEffect` hook to load the image blob from the provided source URL.
 * - The `FileReader` API is used to convert the blob into a data URL for rendering the image.
 * - Clicking the image triggers a modal to open and logs a telemetry event.
 *
 * @example
 * ```tsx
 * <ServerMessageImage
 *   src="https://example.com/image.jpg"
 *   alt="Example Image"
 *   queryResponseId="12345"
 *   onImgLoad={() => console.log('Image loaded')}
 * />
 * ```
 */
const ServerMessageImage = ({
    src,
    alt,
    queryResponseId,
    onImgLoad,
}: ServerMessageImageProps): JSX.Element => {
    const [imageUrl, setImageUrl] = useState<string>('');
    const openImageModal = useContext(ImageModalContext);
    const chatService = ChatBlkQueryService.getInstance();
    const appContext = chatService.appContext;

    /**
     * Load the image blob from the source URL. When the image is loaded, set the image URL.
     */
    useEffect(() => {
        const loadBlob = async () => {
            const imgBlob: Blob = await getStore(appContext)?.get(src);
            const reader = new FileReader();
            reader.onload = () => {
                setImageUrl(reader.result as string);
            };
            reader.readAsDataURL(imgBlob);
        };
        loadBlob();
    }, [src]);

    const handleImageClick = () => {
        openImageModal?.({ src, alt });
        ChatBlkTelemetryService.track(
            telemetryFunctionMap.CHATBLK_MFE_FUNCTION_QUERY_RESPONSE_IMAGE_CLICKED,
            { src, alt },
            queryResponseId
        );
    };

    return (
        <img
            src={imageUrl}
            alt={alt}
            onClick={handleImageClick}
            onLoad={onImgLoad}
        />
    );
};

export default ServerMessageImage;
